import { FormAdapter } from "hooks/useForm";
import { useSimpleRequest } from "hooks";
import { updateOrCreateResource } from "infrastructure/api";
import { ApiRoutes } from "consts/enpoints/api";
import { ECommerceOnboardingFilableResource } from "consts/ecommerce";
import { WebRoutes } from "consts/enpoints/web";
import type { HighRiskMerchant as HighRiskMerchantType, WithUser } from "features/ecommerce/types";
import { HighRiskMerchant as HighRiskMerchantApi } from "features/ecommerce/api";
import { useUser } from "features/auth/useUser";
import { HighRiskMerchant } from "ui/forms/ECommerce";
import { useOnboardingFileUpload, useOnboardingForm, useOnboardingStepData } from "ui/organizms/ECommerce/Onboarding/common";
import { useOnboardingApplication } from "../../hooks";

export const useUploadDocuments = () => {
    const { getApplicationModel } = useOnboardingApplication();

    const { user } = useUser();

    const { data } = useOnboardingStepData<HighRiskMerchantType.OnboardingUploadDocuments>();

    const uploadFiles = useOnboardingFileUpload();

    const request = useSimpleRequest();

    const withValues = useOnboardingForm({
        save: HighRiskMerchantApi.putOnboardingsHighRiskUploadDocumentsSteps,
        route: WebRoutes.Onboarding.HighRiskMerchant,
        getApplicationModel
    });

    const formProps: HighRiskMerchant.UploadDocuments.OnboardingUploadDocumentsForm = {
        bootstrapData: {},
        initialValues: HighRiskMerchant.UploadDocuments.getInitialValues(data),
        isBootstrapDataLoading: false,
        onSaveOrCreate: async (values, helpers) => {
            try {
                for (const documents of Object.values(values)) {
                    for (const document of documents!) {
                        if (document.documentFileKey instanceof File) {
                            await request<HighRiskMerchantType.Document>(() => updateOrCreateResource<WithUser & HighRiskMerchantType.Document>({
                                getRoute: () => ApiRoutes.PutOnboardingsHighRiskClientDocuments(user.coreId),
                                body: {
                                    userId: user.coreId,
                                    documentType: document.documentType
                                }
                            }), {
                                onError: response => {
                                    throw response;
                                },
                                onSuccess: ({ data }) => uploadFiles({
                                    resource: ECommerceOnboardingFilableResource.ClientDocuments,
                                    identifier: data.pki
                                }, [
                                    document.documentFileKey, HighRiskMerchant.UploadDocuments.DocumentField.DocumentFileKey
                                ])
                            })
                        }
                    }
                }

                return withValues({}, helpers);
            } catch (failedfileResponse) {
                return FormAdapter.fromResponse(failedfileResponse)
            }
        }
    };

    return {
        ...formProps,
        ...HighRiskMerchant.UploadDocuments.useForm(formProps),
        ...data
    };
};

export default useUploadDocuments;
